import { Fragment, useCallback, useEffect, useState } from "react";
import { Badge, Button, Collapse, Flex, Loader, Table, Text } from "@mantine/core";
import { useNavigate } from "react-router";
import styled from "styled-components";
import ScrollBox from "../../common/Table/ScrollBox";
import PlusIcon from "../../common/Icons/PlusIcon";
import TableDeleteButton from "../../common/Buttons/TableDeleteButton";
import TableInfoButton from "../../common/Buttons/TableInfoButton";
import AreYouSureModal from "../../common/Modals/AreYouSureModal";
import ShiftTeamFormModal, {
  EMPTY_TEAM_FORM,
  ShiftTeamFormState,
  teamFormOf,
} from "../../Shifts/ShiftTeamFormModal";
import {
  ShiftTeam,
  ShiftTeamMember,
  deleteShiftTeam,
  getShiftTeamMembers,
  getShiftTeams,
} from "../../../api/shiftRotationApi";
import { createNotification } from "../../../utils/useNotification";

/**
 * DEPARTMAN → EKİPLER.
 *
 * NEDEN BURADA: ekip departmansız olamaz (`shiftTeamValidator` `departmentId`'yi
 * zorunlu tutuyor), yani departmanın içinde ekipleri göstermek uydurma bir
 * hiyerarşi değil, var olanı göstermek. Departman 2.35'te çalışma düzenini
 * bırakınca ad + açıklamadan ibaret kalmıştı; içini dolduran şey bu.
 *
 * ⚠ ÜYE EKLEME BURADA DEĞİL, BİLEREK. Üyelik ekibin ya da personelin üstünde bir
 * alan değil, `shiftAssignments` içinde TARİH ARALIKLI bir kayıt: "Ahmet 1
 * Eylül'den itibaren A ekibinde". Buraya ikinci bir ekleme akışı koymak personel
 * listesini, süzgeçleri, sayfalamayı ve çakışma/"öncekini kapat" akışını ikinci
 * kez yazdırırdı. Onun yerine "Üyeleri yönet" mevcut **Personel Atama** ekranını
 * ekip kipinde, departman ve ekip önceden seçili açar.
 */

interface Props {
  departmentId: string;
  departmentName: string;
}

const DepartmentTeams = ({ departmentId, departmentName }: Props) => {
  const navigate = useNavigate();

  const [teams, setTeams] = useState<ShiftTeam[]>([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<ShiftTeamFormState>(EMPTY_TEAM_FORM);

  const [deleteTarget, setDeleteTarget] = useState<ShiftTeam | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  /** Açık olan ekibin id'si — üyeler yalnızca açılınca çekilir. */
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [members, setMembers] = useState<Record<string, ShiftTeamMember[]>>({});
  const [membersLoading, setMembersLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      // Departmanın ekip sayısı avuç içi kadar; sayfalama gereksiz sürtünme olurdu.
      const data: any = await getShiftTeams({ departmentId, limit: 100 });
      setTeams(data?.data ?? []);
    } catch {
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, [departmentId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleMembers = async (teamId: string) => {
    if (expandedId === teamId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(teamId);
    if (members[teamId]) return;

    setMembersLoading(true);
    try {
      const data: any = await getShiftTeamMembers(teamId);
      setMembers((prev) => ({ ...prev, [teamId]: data?.data ?? [] }));
    } catch {
      setMembers((prev) => ({ ...prev, [teamId]: [] }));
    } finally {
      setMembersLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const response: any = await deleteShiftTeam(deleteTarget._id);
    createNotification({
      success: response?.success,
      message: response?.success ? "Ekip silindi." : response?.message,
    });
    if (response?.success) await load();
  };

  // Personel Atama ekranını ekip kipinde, departman ve ekip seçili açar.
  const manageMembers = (teamId: string) =>
    navigate(
      `/shifts/shift-assignment?mode=team&team=${teamId}&department=${departmentId}`
    );

  if (loading) {
    return (
      <Flex justify="center" py={40}>
        <Loader color="#5C26FF" />
      </Flex>
    );
  }

  return (
    <>
      <Flex justify="space-between" align="center" mb={16} gap={16} wrap="wrap">
        <Text size="sm" c="dimmed" style={{ maxWidth: 620 }}>
          Ekip, <strong>{departmentName}</strong> içinde dönüşümlü çalışan
          personel kümesidir. Hangi ekibin hangi hafta hangi vardiyada olduğu{" "}
          <strong>Vardiyalar → Rotalar</strong> ekranında kurulur.
        </Text>
        <Button
          color="#5C26FF"
          radius={10}
          leftSection={<PlusIcon />}
          onClick={() => {
            setForm({ ...EMPTY_TEAM_FORM, departmentId });
            setFormOpen(true);
          }}
        >
          Yeni Ekip
        </Button>
      </Flex>

      {teams.length === 0 ? (
        <EmptyState>
          Bu departmanda henüz ekip yok. Dönüşümlü vardiya kurmak için önce
          ekipleri açın (ör. <strong>A Ekibi</strong>, <strong>B Ekibi</strong>),
          sonra <strong>Rotalar</strong> ekranından çevrimi kurun.
        </EmptyState>
      ) : (
        <TableWrapper>
          <Table style={{ borderCollapse: "separate" }} verticalSpacing="sm">
            <Table.Thead style={{ background: "#EEF2FF" }}>
              <Table.Tr>
                <StyledTh style={{ width: "12%" }}>Kod</StyledTh>
                <StyledTh style={{ width: "38%" }}>Ekip Adı</StyledTh>
                <StyledTh style={{ width: "20%" }}>Personel</StyledTh>
                <StyledTh style={{ width: "30%" }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {teams.map((team) => (
                <Fragment key={team._id}>
                  <StyledTr onClick={() => toggleMembers(team._id)}>
                    <Table.Td>
                      <Badge variant="light" color="indigo">
                        {team.code}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{team.name}</Table.Td>
                    <Table.Td style={{ color: "#4b5563" }}>
                      {/* Bugün geçerli atamalara göre; ayrılmış kişi sayılmıyor. */}
                      {team.memberCount ?? 0} kişi
                    </Table.Td>
                    <Table.Td>
                      <Flex justify="flex-end" align="center" gap={6}>
                        <Button
                          size="compact-sm"
                          variant="light"
                          color="#5C26FF"
                          onClick={(e) => {
                            e.stopPropagation();
                            manageMembers(team._id);
                          }}
                        >
                          Üyeleri yönet
                        </Button>
                        <TableInfoButton
                          onClick={(e: any) => {
                            e.stopPropagation();
                            setForm(teamFormOf(team));
                            setFormOpen(true);
                          }}
                        />
                        <TableDeleteButton
                          onClick={(e: any) => {
                            e.stopPropagation();
                            setDeleteTarget(team);
                            setDeleteOpen(true);
                          }}
                        />
                      </Flex>
                    </Table.Td>
                  </StyledTr>
                  <Table.Tr>
                    <Table.Td colSpan={4} style={{ padding: 0, border: 0 }}>
                      <Collapse in={expandedId === team._id}>
                        <MemberBox>
                          {membersLoading && !members[team._id] ? (
                            <Loader size="sm" color="#5C26FF" />
                          ) : (members[team._id] ?? []).length === 0 ? (
                            <Text size="sm" c="dimmed">
                              Bugün bu ekipte kimse yok. "Üyeleri yönet" ile
                              personel ekleyebilirsiniz.
                            </Text>
                          ) : (
                            <Flex direction="column" gap={6}>
                              <Text size="xs" c="dimmed">
                                Bugünkü üyeler — üyelik tarih aralıklıdır, liste
                                seçilen güne göre değişir.
                              </Text>
                              <Flex gap={8} wrap="wrap">
                                {(members[team._id] ?? []).map((member) => (
                                  <Badge
                                    key={member.assignmentId}
                                    variant="light"
                                    color="gray"
                                    styles={{
                                      label: {
                                        textTransform: "none",
                                        fontWeight: 500,
                                      },
                                    }}
                                  >
                                    {member.name} · {member.startDate}
                                    {member.endDate ? ` → ${member.endDate}` : ""}
                                  </Badge>
                                ))}
                              </Flex>
                            </Flex>
                          )}
                        </MemberBox>
                      </Collapse>
                    </Table.Td>
                  </Table.Tr>
                </Fragment>
              ))}
            </Table.Tbody>
          </Table>
        </TableWrapper>
      )}

      <ShiftTeamFormModal
        opened={formOpen}
        onClose={() => setFormOpen(false)}
        form={form}
        setForm={setForm}
        departmentOptions={[]}
        lockDepartment
        onSaved={load}
      />

      <AreYouSureModal
        opened={deleteOpen}
        setOpened={setDeleteOpen}
        proccessFunc={handleDelete}
        title="Ekibi Sil"
        text={
          `"${deleteTarget?.name}" ekibini silmek istediğinizden emin misiniz? ` +
          "Ekip bir rotada ya da personel atamasında kullanılıyorsa silme işlemi reddedilir."
        }
        confirmButtonText="Sil"
        cancelButtonText="Vazgeç"
      />
    </>
  );
};

export default DepartmentTeams;

const TableWrapper = styled(ScrollBox)`
  overflow-y: auto;
  min-height: max-content;
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const EmptyState = styled.div`
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  padding: 32px;
  color: #6b7280;
  font-size: 15px;
  line-height: 22px;
`;

const MemberBox = styled.div`
  background: #fafafa;
  border-top: 1px solid #eef2f7;
  padding: 14px 16px;
`;

const StyledTh = styled(Table.Td)`
  color: #4c4c4c;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
`;

const StyledTr = styled(Table.Tr)`
  cursor: pointer;
  color: #111827;
  font-size: 16px;
  font-weight: 400;
  &:hover {
    background: #f1f3f5 !important;
  }
`;
